import { client } from "api/client";
import * as actions from "modules/common/store/actions";
import { CONFIGURATION } from "utilities/configuration";
import * as utility from "utilities/utils";
import { CONSTANTS } from "utilities/constants";
import contentHelper from "utilities/contentHelper";
import commonUtilities from "utilities/common";
import { ENDPOINTS } from "utilities/endPoints";

function isValidItinerary(itinerary, today, allowedStatuses) {
    const status = itinerary.status?.toLowerCase() || "";
    return (
        itinerary.itineraryValidDate &&
        commonUtilities.calculateDuration(today, itinerary.itineraryValidDate) >= 0 &&
        allowedStatuses.includes(status)
    );
}

function isAdultOnlyItinerary(itinerary, productTypeCode) {

    if (productTypeCode !== CONSTANTS.FLIGHT && productTypeCode !== CONSTANTS.DEVIATION) {
        return !(itinerary?.itineraryDetail?.roomType?.pricing?.some(p => p.ageRange));
    }
    else {
        return false;
    }
}

function initItinerary(itinerary, productTypeCode, counters) {
    const nextTotal = counters.total + 1;
    const hidden = nextTotal > CONFIGURATION.CUSTOM_QUOTE_ITINERARY_PAGE_SIZE;

    const itineraryValidDate =  itinerary.itineraryValidDate;

    const adultOnly = isAdultOnlyItinerary(itinerary, productTypeCode);

    return {
        ...itinerary,
        markup: utility.getMarkupObject(),
        isExpanded: false,
        isConcessionExpanded: false,
        included: true,
        hidden,
        adultOnly,
        validDate: itineraryValidDate,
        originalValidDate: itineraryValidDate,
        showValidDateWarningIcon: false,
        _counterUpdate: {
            total: nextTotal,
            visible: counters.visible + (hidden ? 0 : 1)
        }
    };
}

function buildCustomConcessions(roomType) {
    if (
        !roomType?.concessionDisplayText?.length ||
        roomType.hideRoomConcession
    ) {
        return roomType;
    }

    let concessionIndex = 0;

    return {
        ...roomType,
        customConcessions: roomType.concessionDisplayText
            .filter(t => t?.trim())
            .map(text => ({
                included: false,
                concessionIndex: concessionIndex++,
                concessionDisplayText: text,
                originalText: text,
                placeHolderObjects: contentHelper.getPlaceHolderObjects(text)
            }))
    };
}

/**
 * To initialize the response for customized quote from a regular quote response.
 *
 * @param {Object} response - regular quote response.
 * @returns {dispatch} a dispatch.
 */
export function getCustomQuote(response) {
    
    return dispatch => {
        if (!response || response.type !== actions.GET_QUOTE_SUCCESS) {
            return dispatch({
                type: actions.SET_CUSTOM_QUOTE_FAIL,
                payload: response?.payload
            });
        }

        const closedResponse = structuredClone(response);
        const TODAY = commonUtilities.getTodayDate();
        const quote = closedResponse.payload.data;

        let counters = { total: 0, visible: 0 };

        const quoteOptions = quote?.quoteOptions?.reduce((acc, option) => {

            const productTypeCode = option?.productType?.code?.toLowerCase();
            let updatedOption = { ...option };

            // ===== DEVIATION =====
            if (productTypeCode === CONSTANTS.DEVIATION) {
                return acc; // Skip deviation options for custom quotes
                const itinerary = option.deviationItinerary;

                if (
                    isValidItinerary(itinerary, TODAY, ["", "adjusted"])
                ) {
                    const newItinerary = initItinerary(itinerary, productTypeCode, counters);
                    counters = newItinerary._counterUpdate;

                    updatedOption.deviationItinerary = {
                        ...newItinerary
                    };

                    acc.push(updatedOption);
                }
                return acc;
            }

            // ===== PRODUCTS =====
            const products = option.products?.reduce((pAcc, product) => {
                const itineraries = product.itineraries
                    ?.filter(it =>
                        isValidItinerary(it, TODAY, ["", "adjusted", "manual"])
                    )
                    .map(it => {
                        const newIt = initItinerary(it, productTypeCode, counters);
                        counters = newIt._counterUpdate;

                        return {
                            ...newIt,
                            itineraryDetail: {
                                ...newIt.itineraryDetail,
                                roomType: buildCustomConcessions(
                                    newIt.itineraryDetail?.roomType
                                )
                            }
                        };
                    });

                if (itineraries?.length) {
                    pAcc.push({ ...product, itineraries });
                }

                return pAcc;
            }, []);

            if (products?.length) {
                acc.push({ ...updatedOption, products });
            }

            return acc;
        }, []);

        // ===== FINAL QUOTE SHAPING =====
        const finalQuote = {
            ...quote,
            quoteOptions,
            totalItineraries: counters.total,
            showItinerariesSelected: counters.visible,
            markupCoverage: CONSTANTS.MARKUP_COVERAGE_ALL,
            markup: utility.getMarkupObject(),
            additionalRequirements: "",
            originalAdditionalRequirements: ""
        };

        finalQuote.promotions = finalQuote.promotions?.map(p => ({
            ...p,
            included: false,
            originalText: p.description,
            placeHolderObjects: contentHelper.getPlaceHolderObjects(p.description)
        }));

        finalQuote.termsAndConditions = finalQuote.termsAndConditions
            ?.slice(0, CONFIGURATION.CUSTOM_QUOTE_STEP5_TABS)
            .map((t, index) => ({
                ...t,
                isExpanded: false,
                inEditing: false,
                isChanged: false,
                isReviewed: index === 0,
                originalText: t.description
            }));

        return dispatch({
            type: actions.SET_CUSTOM_QUOTE_SUCCESS,
            payload: utility.getResponse({
                ...finalQuote
            })
        });
    };
}

/**
 * To call quote processor api to generate pdf for custom quote.
 *
 * @param {Object} params - custom quote response, the model is same as regular response.
 * @returns {dispatch} a dispatch.
 */
export function generateCustomQuotePdf(params) {

    return async (dispatch) => {
        const url = `${process.env.REACT_APP_QUOTE_PROCESSOR}${ENDPOINTS.QUOTE_PROCESSOR.CUSTOM_QUOTE_PDF_GET}`;
        try {
            const response = await client.post(url, params);
            if(response.statusCode === 200) {

                const blob = new Blob(response?.pdfContent, { type: "application/pdf" });
                const url = URL.createObjectURL(blob);
                
                return dispatch({
                    type: actions.GET_CUSTOM_QUOTE_PDF_SUCCESS,
                    payload: utility.getResponse({ statusCode: response.statusCode, pdfURL: url })
                });
            }
            else {
                return dispatch({
                    type: actions.GET_CUSTOM_QUOTE_PDF_FAIL,
                    payload: utility.getErrorResponse(response.errorInfo)
                });
            }
        }
        catch (error) {
            return dispatch({
                type: actions.GET_CUSTOM_QUOTE_PDF_FAIL,
                payload: utility.getErrorResponse(error)
            });
        }
    };
};

function delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}


export function updateCustomQuote(customQuote) {

    return async (dispatch) => {
        return dispatch({
            type: actions.UPDATE_CUSTOM_QUOTE,
            payload: utility.getResponse(customQuote)
        });

    };

}